import {createAsyncThunk} from '@reduxjs/toolkit';
import {normalize, Schema} from 'normalizr';
import {isArray, isBoolean, isEmpty} from 'lodash';
import AppError from '~/utils/error-handler';
import {parseFormDataToObj, serializeQueryString, snakeCaseObj} from '~/utils/helpers/functions';
import request from '~/utils/request';
import {AppDispatch} from '~/redux/root-store';
import {RootState} from '~/redux/root-reducer';
import {
  ApiResponse,
  AxiosResult,
  BaseRequest,
  Callbacks,
  DeleteParams,
  FetchParams,
  NormalizedPayload,
  PostParams,
  PutParams,
  RejectErrorValue,
  ThunkOptions,
  ThunkRawResult,
  ThunkResult,
} from '~/utils/thunk-api/type';
import {requestDummyData} from '~/dummy-data';
import config from '~/config/app-config';

export class ThunkApi<ApiItem = undefined, NormalizedResult = undefined> {
  namespace: string;

  entitySchema?: Schema;

  isSnakeCase: boolean;

  constructor(namespace: string, entitySchema: Schema | undefined = undefined, isSnakeCase = true) {
    this.namespace = namespace;
    this.entitySchema = entitySchema;
    this.isSnakeCase = isSnakeCase;
  }

  fetchOne<Params extends FetchParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {withoutNormalize: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkRawResult<ApiResponseData, Params>;
  fetchOne<Params extends FetchParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {isCallApiOnly: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): AxiosResult<ApiResponseData, Params>;
  fetchOne<Params extends FetchParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  fetchOne<Params extends FetchParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  fetchOne<Params extends FetchParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ) {
    return this.fetchRequest<Params, ApiResponseData>(
      endpoint,
      prefix ? `${prefix}/fetchOne` : 'fetchOne',
      options,
      callbacks,
    );
  }

  fetch<Params extends FetchParams, ApiResponseData = ApiItem[]>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {withoutNormalize: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkRawResult<ApiResponseData, Params>;
  fetch<Params extends FetchParams, ApiResponseData = ApiItem[]>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {isCallApiOnly: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): AxiosResult<ApiResponseData, Params>;
  fetch<Params extends FetchParams, ApiResponseData = ApiItem[]>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  fetch<Params extends FetchParams, ApiResponseData = ApiItem[]>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  fetch<Params extends FetchParams, ApiResponseData = ApiItem[]>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ) {
    return this.fetchRequest<Params, ApiResponseData>(
      endpoint,
      prefix ? `${prefix}/fetchMany` : 'fetchMany',
      options,
      callbacks,
    );
  }

  post<Params extends PostParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {withoutNormalize: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkRawResult<ApiResponseData, Params>;
  post<Params extends PostParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {isCallApiOnly: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): AxiosResult<ApiResponseData, Params>;
  post<Params extends PostParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  post<Params extends PostParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  post<Params extends PostParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ) {
    return this.postRequest<Params, ApiResponseData>(
      endpoint,
      prefix ? `${prefix}/post` : 'post',
      {...options, restfulMethod: 'post'},
      callbacks,
    );
  }

  put<Params extends PutParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {withoutNormalize: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkRawResult<ApiResponseData, Params>;
  put<Params extends PutParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {isCallApiOnly: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): AxiosResult<ApiResponseData, Params>;
  put<Params extends PutParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  put<Params extends PutParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  put<Params extends PutParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ):
    | ThunkResult<NormalizedResult, Params>
    | AxiosResult<ApiResponseData, Params>
    | ThunkRawResult<ApiResponseData, Params> {
    return this.postRequest<Params, ApiResponseData>(
      endpoint,
      prefix ? `${prefix}/put` : 'put',
      {...options, restfulMethod: 'put'},
      callbacks,
    );
  }

  delete<Params extends DeleteParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {withoutNormalize: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkRawResult<ApiResponseData, Params>;
  delete<Params extends DeleteParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions & {isCallApiOnly: true},
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): AxiosResult<ApiResponseData, Params>;
  delete<Params extends DeleteParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix: string,
    options: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  delete<Params extends DeleteParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ): ThunkResult<NormalizedResult, Params>;
  delete<Params extends DeleteParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ) {
    const deletePrefix = prefix ? `${this.namespace}/${prefix}/delete` : `${this.namespace}/delete`;
    if (options?.isCallApiOnly === true) {
      return this.createApiRequestThunk<ApiResponseData, Params>(
        endpoint,
        {...options, restfulMethod: 'delete'},
        callbacks,
      );
    }

    if (options?.withoutNormalize === true) {
      return this.createAsyncThunkWithoutNormalize<ApiResponseData, Params>(
        deletePrefix,
        endpoint,
        {...options, restfulMethod: 'delete'},
        callbacks,
      );
    }
    return this.createAsyncThunkAndNormalize<ApiResponseData, Params>(
      deletePrefix,
      endpoint,
      {...options, restfulMethod: 'delete'},
      callbacks,
    );
  }

  postFormData = <Params extends PostParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ) =>
    this.post(
      endpoint,
      prefix,
      {requestConfig: {headers: {'content-type': 'multipart/form-data'}}, ...options, restfulMethod: 'post'},
      callbacks,
    );

  putFormData = <Params extends PutParams, ApiResponseData = ApiItem>(
    endpoint: string,
    prefix?: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ) =>
    this.put(
      endpoint,
      prefix,
      {requestConfig: {headers: {'content-type': 'multipart/form-data'}}, ...options, restfulMethod: 'put'},
      callbacks,
    );

  misc = <P, Return = any>(
    prefix: string,
    cb: (arg1: P, dispatch: AppDispatch, onError: (e?: AppError) => any, getState: any) => Promise<Return>,
  ): ThunkRawResult<Return, P> =>
    createAsyncThunk<Return, P, {dispatch: AppDispatch; state: RootState}>(
      `${this.namespace}/MISC/${prefix}`,
      async (params, {dispatch, rejectWithValue, getState}) => {
        const onError = (appErr?: AppError | any) => {
          if (!appErr) {
            return rejectWithValue({});
          }
          if (!(appErr instanceof AppError)) {
            appErr = new AppError(appErr);
          }
          return rejectWithValue({
            errCode: appErr.customErrCode,
            errStatusCode: appErr.statusCode,
            messageBag: appErr.messageBag,
            contexts: appErr.contexts,
            errMsg: config.stage === 'production' ? appErr.userMsg : `${appErr.userMsg} - ${appErr.originalErrMsg}`,
          } as RejectErrorValue);
        };
        try {
          return await cb(params, dispatch, onError, getState);
        } catch (e) {
          return onError(e);
        }
      },
    );

  private fetchRequest<Params extends FetchParams, ApiResponseData>(
    endpoint: string,
    prefix: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ):
    | ThunkResult<NormalizedResult, Params>
    | AxiosResult<ApiResponseData, Params>
    | ThunkRawResult<ApiResponseData, Params> {
    if (options?.isCallApiOnly === true) {
      return this.createApiRequestThunk<ApiResponseData, Params>(endpoint, options, callbacks);
    }

    if (options?.withoutNormalize === true) {
      return this.createAsyncThunkWithoutNormalize<ApiResponseData, Params>(prefix, endpoint, options, callbacks);
    }

    return this.createAsyncThunkAndNormalize<ApiResponseData, Params>(prefix, endpoint, options, callbacks);
  }

  private postRequest<Params, ApiResponseData>(
    endpoint: string,
    prefix: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ):
    | ThunkResult<NormalizedResult, Params>
    | AxiosResult<ApiResponseData, Params>
    | ThunkRawResult<ApiResponseData, Params> {
    if (options?.isCallApiOnly === true) {
      return this.createApiRequestThunk<ApiResponseData, Params>(endpoint, options, callbacks);
    }

    if (options?.withoutNormalize === true) {
      return this.createAsyncThunkWithoutNormalize<ApiResponseData, Params>(prefix, endpoint, options, callbacks);
    }

    return this.createAsyncThunkAndNormalize<ApiResponseData, Params>(prefix, endpoint, options, callbacks);
  }

  private createAsyncThunkAndNormalize<ApiResponseData, Params extends BaseRequest>(
    prefix: string,
    endpoint: string,
    options?: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ) {
    if (this.entitySchema === undefined) {
      throw new Error('Normalize Error: Missing schemaEntity declaration. Locate at class constructor');
    }
    return createAsyncThunk<
      NormalizedPayload<NormalizedResult>,
      Params,
      {dispatch: AppDispatch; rejectValue: RejectErrorValue; state: RootState}
    >(`${this.namespace}/${prefix}`, async (params: Params, {dispatch, rejectWithValue, getState}) => {
      const {source, data: requestData} = params;
      try {
        const responseBody = await this.makeRequest<Params, ApiResponseData>(endpoint, params, options);
        const apiResponseData = (responseBody?.data?.data || []) as ApiResponseData;
        const normalized = normalize<any, NormalizedResult>(
          apiResponseData,
          isArray(apiResponseData) ? [this.entitySchema!] : this.entitySchema!,
        );
        if (requestData instanceof FormData) {
          // @ts-ignore
          params.data = parseFormDataToObj(requestData);
        }
        const payload: NormalizedPayload<NormalizedResult, any, ApiResponseData> = {
          normalized,
          source: source || options?.fixedParams?.source,
          paginator: responseBody?.data?.paginator,
          isLoadMore: params?.isLoadMore,
          apiResponseData: undefined,
        };
        if (options?.includeApiResponseData) {
          payload.apiResponseData = apiResponseData;
        }

        // if (callbacks?.onSuccess) {
        //   await callbacks?.onSuccess({responseBody, returnPayload: payload, params, dispatch, getState});
        // }

        return payload;
      } catch (e) {
        const appErr = new AppError(e);
        return rejectWithValue({
          errCode: appErr.customErrCode,
          errStatusCode: appErr.statusCode,
          contexts: appErr.contexts,
          messageBag: appErr.messageBag,
          errMsg: config.stage === 'production' ? appErr.userMsg : `${appErr.userMsg} - ${appErr.originalErrMsg}`,
        } as RejectErrorValue);
      }
    });
  }

  private createAsyncThunkWithoutNormalize<ApiResponseData, Params>(
    prefix: string,
    endpoint: string,
    options: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ) {
    return createAsyncThunk<ApiResponseData, Params, {dispatch: AppDispatch; state: RootState}>(
      `${this.namespace}/withoutNormalize/${prefix}`,
      async (params, {dispatch, getState, rejectWithValue}) => {
        try {
          const responseBody = await this.makeRequest<Params, ApiResponseData>(endpoint, params, options);
          const apiResponseData = (responseBody?.data?.data || {}) as ApiResponseData;
          // if (callbacks?.onSuccess) {
          //   await callbacks?.onSuccess({responseBody, params, dispatch, getState});
          // }
          return apiResponseData;
        } catch (e) {
          const appErr = new AppError(e);
          return rejectWithValue({
            errCode: appErr.customErrCode,
            errStatusCode: appErr.statusCode,
            messageBag: appErr.messageBag,
            contexts: appErr.contexts,
            errMsg: config.stage === 'production' ? appErr.userMsg : `${appErr.userMsg} - ${appErr.originalErrMsg}`,
          } as RejectErrorValue);
        }
      },
    );
  }

  private createApiRequestThunk<ApiResponseData, Params>(
    endpoint: string,
    options: ThunkOptions,
    callbacks?: Callbacks<ApiResponse<ApiResponseData>, NormalizedPayload<NormalizedResult>, Params>,
  ) {
    return async (params: Params) => {
      const responseBody = await this.makeRequest<Params, ApiResponseData>(endpoint, params, options);
      // if (callbacks?.onSuccess) {
      //   await callbacks?.onSuccess({responseBody, params});
      // }
      return responseBody;
    };
  }

  private async makeRequest<Params extends BaseRequest, ApiResponseData>(
    endpoint: string,
    params: Params,
    options?: ThunkOptions,
  ) {
    // Cheating way to differentiate post params and  url params. I think we should depend on the object type later
    const {data: dataParam, ...urlParams} = params;
    let data = {...dataParam, ...options?.fixedData};
    const shouldUseSnakeCase = isBoolean(options?.isSnakeCase) ? options?.isSnakeCase : this.isSnakeCase;
    if (!isEmpty(data)) {
      if (!(data instanceof FormData)) {
        if (shouldUseSnakeCase) {
          data = snakeCaseObj(data);
        }
      }
    }
    const url = getFullUrl(endpoint, {...urlParams, ...options?.fixedParams}, shouldUseSnakeCase);
    if (options?.dummyData) {
      return (await requestDummyData(options?.restfulMethod || 'get', url)) as ApiResponse<ApiResponseData>;
    }
    if (options?.restfulMethod === 'put' || options?.restfulMethod === 'post') {
      const requestApi = options?.restfulMethod === 'put' ? request.put : request.post;
      return (await requestApi(url, data, options?.requestConfig)) as ApiResponse<ApiResponseData>;
    }
    if (options?.restfulMethod === 'delete') {
      return (await request.delete(url, options?.requestConfig)) as ApiResponse<ApiResponseData>;
    }
    return (await request.get(url, options?.requestConfig)) as ApiResponse<ApiResponseData>;
  }
}

const getUrlParamKeys = (url: string): string[] => {
  const urlParamRgx = /:([a-zA-Z0-9]+)/g;
  const urlParamKeys = [];
  let matches;
  // eslint-disable-next-line no-cond-assign
  while ((matches = urlParamRgx.exec(url)) !== null) {
    urlParamKeys.push(matches[1]);
  }

  return urlParamKeys;
};

const getFullUrl = (endpoint: string, params: FetchParams, isUseSnakeCase?: boolean) => {
  const {source, ...rest} = params;
  let url = endpoint;
  getUrlParamKeys(url).forEach(urlParamKey => {
    url = url.replace(`:${urlParamKey}`, params[urlParamKey]);
  });

  url += serializeQueryString({...rest}, {snakeKey: isUseSnakeCase});
  return url;
};
